/**
 * EmployeeDetail — drill-down view showing employee info, assigned assets,
 * site details, Intune/NinjaOne device health, and coworkers.
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { SecurityInvestigation } from '@/components/SecurityInvestigation';

async function fetchEmployee(id: string) {
  const res = await fetch(`/api/employees/${id}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to load employee');
  return res.json();
}

export function EmployeeDetail() {
  const { id } = useParams<{ id: string }>();
  const [showSecurityAudit, setShowSecurityAudit] = useState(false);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => fetchEmployee(id!),
    enabled: !!id,
  });

  if (isLoading) return <LoadingSpinner message="Loading employee…" />;
  if (error || !data) return <ErrorMessage message={(error as Error)?.message ?? 'Not found'} onRetry={() => refetch()} />;

  const emp = data.employee as Record<string, unknown>;
  const assets = (data.assets ?? []) as Record<string, unknown>[];
  const coworkers = (data.coworkers ?? []) as Record<string, unknown>[];
  const orgChart = data.orgChart as { manager: Record<string, unknown> | null; directReports: Record<string, unknown>[] } | null;

  const siteCode = String(emp.ResolvedSiteCode ?? '');
  const branchName = String(emp.BranchName ?? '');

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <Link to="/employees" className="text-slate-500 no-underline text-sm hover:text-baker-red">← Employees</Link>
        <div className="flex items-center justify-between mt-1">
          <h1 className="text-lg font-semibold text-text-primary">{String(emp.LegalName)}</h1>
          {Boolean(emp.WorkEmail) && (
            <button
              onClick={() => setShowSecurityAudit(true)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-baker-red rounded hover:bg-red-700 transition-colors"
            >
              Security Audit
            </button>
          )}
        </div>
        <div className="flex gap-2 mt-2 flex-wrap">
          {emp.EmployeeCategory ? <StatusBadge label={String(emp.EmployeeCategory)} variant="blue" /> : null}
          {emp.FullTimePartTime ? <StatusBadge label={emp.FullTimePartTime === 'F' ? 'Full-Time' : 'Part-Time'} variant="gray" /> : null}
          {emp.EmployeeStatus ? <StatusBadge label={String(emp.EmployeeStatus)} variant={emp.EmployeeStatus === 'Active' ? 'green' : 'red'} /> : null}
        </div>
      </div>

      {/* Security Investigation Modal */}
      {showSecurityAudit && Boolean(emp.WorkEmail) && (
        <SecurityInvestigation
          email={String(emp.WorkEmail)}
          onClose={() => setShowSecurityAudit(false)}
        />
      )}

      {/* Employee Info */}
      <section className="mb-6 bg-white rounded-lg border border-border p-5">
        <h2 className="text-base font-semibold text-text-primary mb-3">Employee Information</h2>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4 text-sm">
          <Field label="Baker ID" value={String(emp.BakerId ?? '—')} />
          <Field label="Job Title" value={String(emp.JobTitle ?? '—')} />
          <Field label="Business Title" value={String(emp.BusinessTitle ?? '—')} />
          <Field label="Email" value={emp.WorkEmail ? String(emp.WorkEmail) : '—'} link={emp.WorkEmail ? `mailto:${emp.WorkEmail}` : undefined} />
          <Field label="Reports To" value={
            orgChart?.manager
              ? String((orgChart.manager as Record<string, unknown>).displayName ?? emp.ReportsToName ?? '—')
              : String(emp.ReportsToName ?? '—')
          } link={
            orgChart?.manager && (orgChart.manager as Record<string, unknown>).employeeId
              ? `/employees/${(orgChart.manager as Record<string, unknown>).employeeId}`
              : undefined
          } />
          <Field label="Hire Date" value={emp.HireDate ? new Date(String(emp.HireDate)).toLocaleDateString() : '—'} />
          <Field label="HRBP" value={String(emp.HRBP ?? '—')} />
        </div>
      </section>

      {/* Site Assignment */}
      <section className="mb-6 bg-white rounded-lg border border-border p-5">
        <h2 className="text-base font-semibold text-text-primary mb-3">Site Assignment</h2>
        {siteCode ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4 text-sm">
            <div>
              <span className="text-slate-500">Site:</span>{' '}
              <Link to={`/sites/${siteCode}`} className="text-baker-red no-underline hover:underline font-medium">
                {siteCode} — {branchName}
              </Link>
            </div>
            {emp.City ? <Field label="City" value={`${emp.City}${emp.State ? `, ${emp.State}` : ''}`} /> : null}
            {emp.Address ? <Field label="Address" value={String(emp.Address)} /> : null}
            {emp.SiteType ? <Field label="Site Type" value={String(emp.SiteType)} /> : null}
          </div>
        ) : (
          <p className="text-sm text-slate-400">No site assignment resolved.</p>
        )}
      </section>

      {/* Assigned Assets */}
      <section className="mb-6 bg-white rounded-lg border border-border p-5">
        <h2 className="text-base font-semibold text-text-primary mb-3">
          Assigned Assets ({assets.length})
        </h2>
        {assets.length === 0 ? (
          <p className="text-sm text-slate-400">No hardware assets found for this employee.</p>
        ) : (
          <div className="space-y-3">
            {assets.map((a) => (
              <div key={String(a.AssetId)} className="bg-page rounded-lg p-4 border border-border-subtle">
                <div className="flex items-center justify-between mb-1">
                  <Link to={`/assets/${a.AssetId}`} className="text-baker-red no-underline hover:underline font-medium text-sm">
                    {String(a.DeviceName ?? a.SerialNumber ?? '—')}
                  </Link>
                  <div className="flex gap-2">
                    {a.NinjaOnline !== null && (
                      <StatusBadge label={a.NinjaOnline ? 'Ninja: Online' : 'Ninja: Offline'} variant={a.NinjaOnline ? 'green' : 'red'} />
                    )}
                    {a.IntuneComplianceState ? (
                      <StatusBadge
                        label={`Intune: ${String(a.IntuneComplianceState)}`}
                        variant={a.IntuneComplianceState === 'compliant' ? 'green' : a.IntuneComplianceState === 'noncompliant' ? 'red' : 'gray'}
                      />
                    ) : null}
                  </div>
                </div>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2 text-[13px] text-slate-600">
                  <span>{String(a.Manufacturer ?? '')} {String(a.Model ?? '')}</span>
                  <span>Serial: <span className="font-mono text-xs">{String(a.SerialNumber ?? '—')}</span></span>
                  <span>Status: {String(a.LifecycleStatus ?? '—')}</span>
                  {a.SiteNumber ? <span>Site: {String(a.SiteNumber)}</span> : null}
                  {a.NinjaLastContact ? <span>Last seen: {new Date(String(a.NinjaLastContact)).toLocaleDateString()}</span> : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Org Chart */}
      {orgChart && (orgChart.manager || orgChart.directReports.length > 0) && (
        <section className="mb-6 bg-white rounded-lg border border-border p-5">
          <h2 className="text-base font-semibold text-text-primary mb-4">Org Chart</h2>
          <div className="flex flex-col items-center gap-2">
            {/* Manager */}
            {orgChart.manager && (
              <>
                <OrgCard
                  person={orgChart.manager}
                  role="Manager"
                />
                <div className="w-px h-6 bg-border" />
              </>
            )}

            {/* Current employee */}
            <div className="border-2 border-baker-accent rounded-lg px-5 py-3 bg-red-50/30 text-center min-w-[220px]">
              <div className="font-semibold text-text-primary text-sm">{String(emp.LegalName)}</div>
              <div className="text-xs text-slate-500">{String(emp.BusinessTitle ?? emp.JobTitle ?? '')}</div>
            </div>

            {/* Direct reports */}
            {orgChart.directReports.length > 0 && (
              <>
                <div className="w-px h-6 bg-border" />
                <div className="text-xs text-slate-400 mb-2">{orgChart.directReports.length} direct report{orgChart.directReports.length !== 1 ? 's' : ''}</div>
                <div className="flex flex-wrap gap-3 justify-center">
                  {orgChart.directReports.map((r, i) => (
                    <OrgCard key={i} person={r} role="Report" />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Recent TDX Tickets */}
      {data.recentTickets && (data.recentTickets as Record<string, unknown>[]).length > 0 && (
        <section className="mb-6 bg-white rounded-lg border border-border p-5">
          <h2 className="text-base font-semibold text-text-primary mb-3">
            Recent Support Tickets ({(data.recentTickets as unknown[]).length})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-1.5 px-2 font-medium text-slate-600">ID</th>
                  <th className="text-left py-1.5 px-2 font-medium text-slate-600">Title</th>
                  <th className="text-left py-1.5 px-2 font-medium text-slate-600">Status</th>
                  <th className="text-left py-1.5 px-2 font-medium text-slate-600">Priority</th>
                  <th className="text-left py-1.5 px-2 font-medium text-slate-600">Created</th>
                </tr>
              </thead>
              <tbody>
                {(data.recentTickets as Record<string, unknown>[]).map((t) => (
                  <tr key={String(t.ticketId)} className="border-b border-border-subtle">
                    <td className="py-1.5 px-2">
                      {t.url ? (
                        <a href={String(t.url)} target="_blank" rel="noopener noreferrer" className="text-baker-red no-underline hover:underline font-mono text-xs">
                          {String(t.ticketId)}
                        </a>
                      ) : String(t.ticketId)}
                    </td>
                    <td className="py-1.5 px-2">{String(t.title ?? '—')}</td>
                    <td className="py-1.5 px-2">
                      <StatusBadge label={String(t.status ?? '—')} variant={String(t.status) === 'Closed' ? 'gray' : String(t.status) === 'New' ? 'blue' : 'green'} />
                    </td>
                    <td className="py-1.5 px-2 text-slate-600">{String(t.priority ?? '—')}</td>
                    <td className="py-1.5 px-2 text-slate-500">{t.createdDate ? new Date(String(t.createdDate)).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Coworkers */}
      {coworkers.length > 0 && (
        <section className="mb-6 bg-white rounded-lg border border-border p-5">
          <h2 className="text-base font-semibold text-text-primary mb-3">
            Coworkers at Site {siteCode} ({coworkers.length})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-1.5 px-2 font-medium text-slate-600">Name</th>
                  <th className="text-left py-1.5 px-2 font-medium text-slate-600">Title</th>
                  <th className="text-left py-1.5 px-2 font-medium text-slate-600">Email</th>
                </tr>
              </thead>
              <tbody>
                {coworkers.map((c) => (
                  <tr key={String(c.EmployeeId)} className="border-b border-border-subtle">
                    <td className="py-1.5 px-2">
                      <Link to={`/employees/${c.EmployeeId}`} className="text-baker-red no-underline hover:underline">
                        {String(c.LegalName)}
                      </Link>
                    </td>
                    <td className="py-1.5 px-2 text-slate-600">{String(c.BusinessTitle ?? c.JobTitle ?? '—')}</td>
                    <td className="py-1.5 px-2">
                      {c.WorkEmail ? <a href={`mailto:${c.WorkEmail}`} className="text-slate-600 no-underline hover:text-baker-red">{String(c.WorkEmail)}</a> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function Field({ label, value, link }: { label: string; value: string; link?: string }) {
  return (
    <div>
      <span className="text-slate-500">{label}:</span>{' '}
      {link ? <a href={link} className="text-baker-red no-underline hover:underline">{value}</a> : value}
    </div>
  );
}


function OrgCard({ person, role }: { person: Record<string, unknown>; role: string }) {
  const name = String(person.displayName ?? '');
  const title = String(person.jobTitle ?? '');
  const email = String(person.mail ?? '');
  const empId = person.employeeId as number | null;

  const content = (
    <div className={`rounded-lg px-4 py-3 text-center min-w-[200px] border transition-all ${
      empId ? 'border-border hover:border-baker-accent/40 hover:shadow-sm cursor-pointer' : 'border-border-subtle'
    } ${role === 'Manager' ? 'bg-slate-50' : 'bg-white'}`}>
      <div className="font-medium text-sm text-text-primary">{name}</div>
      <div className="text-xs text-slate-500">{title}</div>
      {email && <div className="text-xs text-slate-400 mt-0.5">{email}</div>}
    </div>
  );

  if (empId) {
    return <Link to={`/employees/${empId}`} className="no-underline">{content}</Link>;
  }
  return content;
}
